import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import { filter, find, flatten, get, isEmpty, isNull, map, split } from 'lodash'
import PropTypes from 'prop-types'

import { PUBLIC_URL } from '../../common/constants'
import {
  CrackIcon,
  DentIcon,
  Dirt2Icon,
  MultipleDamageIcon,
  RepairFailedIcon,
  RepairSuccesIcon,
  Scratch2Icon,
  ScratchSmallIcon,
  SingleDamageIcon,
} from '../../icons'
import ImageCarousel from '../ImageCarousel'

const DAMAGE_ICONS = {
  Odprysk: <Scratch2Icon />,
  Pęknięcie: <CrackIcon />,
  'Po Naprawie': <RepairSuccesIcon />,
  'Drobna rysa': <ScratchSmallIcon />,
  Rysa: <Scratch2Icon />,
  'Rysa z uszkodzeniem lakieru': <Scratch2Icon />,
  Uszkodzenie: <SingleDamageIcon />,
  'Wgniecenie do 20 mm': <DentIcon />,
  'Wgniecenie powyżej 20 mm': <DentIcon />,
  Zabrudzenie: <Dirt2Icon />,
  'Zła jakość naprawy': <RepairFailedIcon />,
  'Ubytek lakieru': <Scratch2Icon />,
}

const CarMeshImg = styled('img')({
  width: 300,
  height: 424,
})

const CarMeshPointer = styled('span')({
  position: 'absolute',
  zIndex: '100',
  cursor: 'pointer',
})

const CarMeshContainer = styled(Box)({
  position: 'relative',
  width: 300,
  height: 424,
})

const StyledList = styled('ul')({
  listStyleType: 'none',
  margin: 0,
  padding: 0,
})

const StyledListItem = styled('li')({
  '&:before': {
    content: '"- "',
  },
})

const StyledTabButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  height: '100%',
  backgroundColor: theme.palette.common.black,
  color: theme.palette.background.default,
  '&.Mui-active': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
    borderTop: '4px solid',
  },
  '&:not(.Mui-active):hover': {
    backgroundColor: '#444',
    color: theme.palette.background.default,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.gray.dark,
    color: theme.palette.background.default,
    borderTop: '4px solid',
  },
}))

function BodyPaintworkThicknessList() {
  const list = [
    { title: '50-150μm', subtitle: 'Lakier fabryczny' },
    { title: '151-200μm', subtitle: 'Lakierowanie renowacyjne' },
    { title: '200-300μm', subtitle: 'Naprawa i lakierowanie renowacyjne' },
    { title: '300-600μm', subtitle: 'Naprawa blacharska i lakierowanie' },
    { title: '>600μm', subtitle: 'Naprawa blacharska i lakierowanie' },
  ]
  return (
    <List
      sx={{
        pl: 2,
        '&:before': {
          position: 'absolute',
          top: 8,
          left: 0,
          bottom: 8,
          display: 'block',
          content: '" "',
          height: 'calc(100% - 16px)',
          width: 12,
          background:
            'linear-gradient(180deg, #19BF0A 0%, #F1F512 34.76%, #F85C04 66.05%, #DA0000 100%)',
        },
      }}
    >
      {map(list, ({ title, subtitle }, key) => (
        <ListItem
          key={key}
          disablePadding
          sx={{
            padding: '0 !important',
            '&:before': { display: 'none !important' },
          }}
        >
          <ListItemText
            primary={title}
            primaryTypographyProps={{ variant: 'caption' }}
            secondary={subtitle}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      ))}
    </List>
  )
}

function BodyDamagesList() {
  const list = [
    {
      icon: <ScratchSmallIcon />,
      label: 'Drobna rysa',
    },
    {
      icon: <Scratch2Icon />,
      label: 'Odprysk / Rysa / Rysa z uszkodzeniem lakieru / Ubytek lakieru',
    },
    { icon: <CrackIcon />, label: 'Pęknięcie' },
    {
      icon: <DentIcon />,
      label: 'Wgniecenie do 20mm / Wgniecenie powyżej 20mm',
    },
    { icon: <Dirt2Icon />, label: 'Zabrudzenie' },
    { icon: <RepairSuccesIcon />, label: 'Po naprawie' },
    { icon: <RepairFailedIcon />, label: 'Zła jakość naprawy' },
    { icon: <SingleDamageIcon />, label: 'Uszkodzenie' },
    { icon: <MultipleDamageIcon />, label: 'Wiele uszkodzeń' },
  ]
  return (
    <List>
      {map(list, ({ icon, label }, key) => (
        <ListItem
          key={key}
          disablePadding
          sx={{
            padding: '0 !important',
            '&:before': { display: 'none !important' },
          }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </ListItem>
      ))}
    </List>
  )
}

// eslint-disable-next-line react/prop-types
function BodyTooltipDamages({ damages }) {
  return (
    <StyledList>
      {map(damages, (damage, key) => (
        <StyledListItem key={key}>{damage.bodyDamage}</StyledListItem>
      ))}
    </StyledList>
  )
}

// eslint-disable-next-line react/prop-types
function BodyConditionMesh({ bodyConditions, selected }) {
  const { t } = useTranslation()
  const elementsIconsCoords = [
    { bodyElementId: 1, coords: [70, 137] },
    { bodyElementId: 2, coords: [57, 30] },
    { bodyElementId: 3, coords: [307, 48] },
    { bodyElementId: 4, coords: [57, 247] },
    { bodyElementId: 5, coords: [307, 227] },
    { bodyElementId: 6, coords: [170, 38] },
    { bodyElementId: 7, coords: [245, 38] },
    { bodyElementId: 8, coords: [170, 238] },
    { bodyElementId: 9, coords: [245, 238] },
    { bodyElementId: 10, coords: [343, 140] },
    { bodyElementId: 11, coords: [224, 140] },
    { bodyElementId: 12, coords: [5, 137] },
    { bodyElementId: 13, coords: [375, 137] },
    { bodyElementId: 14, coords: [138, 140] },
    { bodyElementId: 15, coords: [301, 140] },
    { bodyElementId: 16, coords: [189, 74] },
    { bodyElementId: 17, coords: [238, 74] },
    { bodyElementId: 18, coords: [189, 202] },
    { bodyElementId: 19, coords: [238, 202] },
    { bodyElementId: 20, coords: [145, 56] },
    { bodyElementId: 21, coords: [145, 220] },
    { bodyElementId: 22, coords: [210, 71] },
    { bodyElementId: 23, coords: [210, 204] },
    { bodyElementId: 24, coords: [220, 10] },
    { bodyElementId: 25, coords: [220, 266] },
  ]
  const bodyConditionsWithCoords = map(bodyConditions, (elem) => {
    const item = find(elementsIconsCoords, {
      bodyElementId: elem.bodyElementId,
    })
    if (item) {
      const mainDamage = find(elem.bodyDamages, { isMain: true }) || false
      return {
        ...elem,
        coords: item.coords,
        mainDamage,
      }
    }
    return elem
  })

  return (
    <CarMeshContainer>
      <CarMeshImg
        src={`${PUBLIC_URL}/assets/images/car_body_mesh.png`}
        alt="carmesh"
      />
      {map(bodyConditionsWithCoords, (element, key) =>
        element.coords ? (
          <CarMeshPointer
            key={key}
            sx={{ top: element.coords[0], left: element.coords[1] }}
            id={element.bodyElementId}
          >
            {selected === 'inspekcja' && element.mainDamage.bodyDamageId !== 1 && (
              <Tooltip
                title={
                  <>
                    {t('damagesTooltipHead', {
                      element: element.bodyElementName,
                    })}
                    <BodyTooltipDamages damages={element.bodyDamages} />
                  </>
                }
                arrow
              >
                <Box component="span">
                  {element.bodyDamages.length > 1 ? (
                    <MultipleDamageIcon />
                  ) : (
                    get(DAMAGE_ICONS, element.mainDamage.bodyDamage)
                  )}
                </Box>
              </Tooltip>
            )}
            {selected === 'pomiar' && (
              <Tooltip title={element.bodyElementName} arrow>
                <Box
                  component="span"
                  sx={(theme) => ({
                    position: 'absolute',
                    transform: 'translateX(-36%)',
                    whiteSpace: 'nowrap',
                    backgroundColor: theme.palette.background.default,
                  })}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={({ palette }) => ({
                      color:
                        palette.paintworkThickness[
                          element.bodyPaintWorkThicknessDictId
                        ],
                    })}
                  >
                    {split(element.bodyPaintWorkThicknessDescription, ' – ')[0]}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </CarMeshPointer>
        ) : null,
      )}
    </CarMeshContainer>
  )
}

function DigitalCertificateBodyCondition({ certificateData, index }) {
  const { t } = useTranslation()
  const { basicData, bodyConditions } = certificateData
  const [selected, setSelected] = useState('inspekcja')
  const images = filter(
    flatten(
      map(bodyConditions, ({ bodyDamages, bodyElementName }) =>
        map(bodyDamages, (damage) =>
          isNull(damage.image)
            ? null
            : {
                url: damage.image.imageUrl,
                name: `${bodyElementName} - ${damage.bodyDamage}`,
              },
        ),
      ),
    ),
    (el) => !isNull(el),
  )

  const handleTabClick = (e) => {
    setSelected(e.target.id)
  }

  return (
    <Grid container spacing={5} sx={{ mt: 0 }}>
      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ mb: 1 }}>
          <Grid container>
            <Grid item xs={6}>
              <StyledTabButton
                id="inspekcja"
                className={selected === 'inspekcja' ? 'Mui-active' : ''}
                fullWidth
                onClick={handleTabClick}
              >
                {t('digital.body.tabs.condition')}
              </StyledTabButton>
            </Grid>
            <Grid item xs={6}>
              <StyledTabButton
                id="pomiar"
                className={selected === 'pomiar' ? 'Mui-active' : ''}
                fullWidth
                onClick={handleTabClick}
              >
                {t('digital.body.tabs.paintwork')}
              </StyledTabButton>
            </Grid>
          </Grid>
        </Paper>
        <Paper elevation={0} sx={{ p: 3 }}>
          {selected === 'inspekcja' && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={index ? 12 : 4}>
                <Typography variant="h4">
                  {t('digital.body.legend.condition')}
                </Typography>
                <BodyDamagesList />
              </Grid>
              <Grid item xs={12} lg={index ? 12 : 8}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BodyConditionMesh
                    bodyConditions={bodyConditions}
                    selected={selected}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
          {selected === 'pomiar' && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={index ? 12 : 4}>
                <Typography variant="h4">
                  {t('digital.body.legend.paintwork')}
                </Typography>
                <BodyPaintworkThicknessList />
              </Grid>
              <Grid item xs={12} lg={index ? 12 : 8}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BodyConditionMesh
                    bodyConditions={bodyConditions}
                    selected={selected}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        {isEmpty(images) ? (
          <img src={basicData.imageUrl} alt="" style={{ width: '100%' }} />
        ) : (
          <ImageCarousel images={images} />
        )}
      </Grid>
    </Grid>
  )
}

DigitalCertificateBodyCondition.propTypes = {
  certificateData: PropTypes.object,
  index: PropTypes.bool,
}

DigitalCertificateBodyCondition.defaultProps = {
  certificateData: null,
  index: false,
}

export default DigitalCertificateBodyCondition
